import { getCsvFieldValuesFromFirstNRecords, getCsvFieldValueFromFirstRecord, parseCsvRows } from '@/features/datasets/utils/csvPreviewUtils';
import { formatOriginalColumnPreview, getFieldValue } from '@/features/datasets/utils/nestedFieldValue';

/**
 * Detect upload file format from a browser File object.
 * Returns 'csv', 'json', 'jsonl', 'parquet', or null if unrecognised.
 */
export function detectFileFormat(file) {
    const lowerName = (file?.name || '').toLowerCase();
    const mime = (file?.type || '').toLowerCase();

    if (mime === 'text/csv' || mime === 'application/vnd.ms-excel' || lowerName.endsWith('.csv')) {
        return 'csv';
    }
    if (lowerName.endsWith('.jsonl') || mime.includes('ndjson') || mime.includes('jsonl')) {
        return 'jsonl';
    }
    if (mime === 'application/json' || lowerName.endsWith('.json')) {
        return 'json';
    }
    if (
        mime === 'application/vnd.apache.parquet' ||
        mime === 'application/x-parquet' ||
        lowerName.endsWith('.parquet')
    ) {
        return 'parquet';
    }
    return null;
}

/**
 * Returns true when the format string represents a JSON-like type (json or jsonl).
 */
export function isJsonLikeFormat(fmt) {
    return fmt === 'json' || fmt === 'jsonl';
}

/**
 * Parse the first JSON object from JSONL (newline-delimited) text.
 * Returns the parsed object or null.
 */
export function parseFirstJsonlObject(text) {
    if (!text) return null;
    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
            const obj = JSON.parse(trimmed);
            if (obj && typeof obj === 'object') return obj;
        } catch { continue; }
    }
    return null;
}

/**
 * Extract up to `maxRows` complete top-level JSON objects from text that may
 * be a truncated pretty-printed array (e.g. `[{...}, {...}, …` cut mid-file).
 * Brace-depth walk mirrors DatasetUploadStepsModal.parseFirstJsonObject.
 */
function _extractJsonObjectsFromPossiblyTruncated(text, maxRows) {
    if (!text || maxRows <= 0) return [];

    const arrayStart = text.indexOf('[');
    let i = arrayStart !== -1 ? arrayStart + 1 : 0;
    const rows = [];

    while (i < text.length && rows.length < maxRows) {
        while (i < text.length && (text[i] === ',' || text[i] === ' ' || text[i] === '\n' || text[i] === '\r' || text[i] === '\t')) {
            i += 1;
        }
        if (i >= text.length || text[i] === ']') break;
        if (text[i] !== '{') {
            i += 1;
            continue;
        }

        let braceCount = 0;
        let inString = false;
        let escapeNext = false;
        const objectStart = i;

        for (; i < text.length; i += 1) {
            const char = text[i];
            if (escapeNext) {
                escapeNext = false;
                continue;
            }
            if (char === '\\') {
                escapeNext = true;
                continue;
            }
            if (char === '"') {
                inString = !inString;
                continue;
            }
            if (inString) continue;
            if (char === '{') braceCount += 1;
            else if (char === '}') {
                braceCount -= 1;
                if (braceCount === 0) {
                    i += 1;
                    try {
                        const obj = JSON.parse(text.slice(objectStart, i));
                        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
                            rows.push(obj);
                        }
                    } catch {
                        // Incomplete / invalid object — stop scanning.
                        return rows;
                    }
                    break;
                }
            }
        }
        if (braceCount !== 0) break;
    }

    return rows;
}

function _parseJsonlLines(text, maxRows) {
    const rows = [];
    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        try {
            rows.push(JSON.parse(trimmed));
        } catch {
            continue;
        }
        if (rows.length >= maxRows) break;
    }
    return rows;
}

function _parseJsonOrJsonlRows(text, maxRows) {
    try {
        const data = JSON.parse(text);
        if (Array.isArray(data)) return data.slice(0, maxRows);
        if (data && typeof data === 'object') return [data];
    } catch {
        // Not one complete JSON document: JSONL, or a truncated chunk.
    }

    // JSONL starts with a record object, not an array. Parse it line by line
    // *before* the truncated-array heuristic: that heuristic seeks the first
    // `[` in the text, which for JSONL is a nested array inside the first
    // record (e.g. agentic `messages`), and would return its elements as rows.
    if (text.trimStart().startsWith('{')) {
        const lines = _parseJsonlLines(text, maxRows);
        if (lines.length) return lines;
    }

    // Truncated pretty-printed JSON arrays fail JSON.parse; extract complete
    // objects by brace depth before falling back to JSONL lines.
    const fromTruncated = _extractJsonObjectsFromPossiblyTruncated(text, maxRows);
    if (fromTruncated.length) return fromTruncated;

    return _parseJsonlLines(text, maxRows);
}

export function decodeBase64UnicodeSafe(base64) {
    if (!base64) return '';
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    try {
        const binary = atob(padded);
        return decodeURIComponent(escape(binary));
    } catch (e) {
        try {
            return atob(padded);
        } catch {
            return '';
        }
    }
}

function takeBase64Chunk(base64, maxLen) {
    if (!base64) return '';
    const chunk = base64.slice(0, Math.min(base64.length, maxLen));
    return chunk + '='.repeat((4 - (chunk.length % 4)) % 4);
}

function collectRawFieldValue(value) {
    if (value === undefined || value === null) return null;
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed || null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return null;
}

export function getFieldValuesFromFirstNRecords({
    base64Content,
    fileType,
    fieldName,
    maxRecords = 6,
    raw = false,
}) {
    if (!base64Content || !fieldName) return [];

    const type = (fileType || '').toLowerCase();

    if (type === 'parquet') return [];

    // Heuristic chunk sizes (base64 chars).
    // - CSV: small chunk is usually enough for first ~10 rows
    // - JSON: larger chunk to capture at least the first object fully
    const isJsonLike = type === 'json' || type === 'jsonl' || type === 'application/json';
    const base64ChunkSize = isJsonLike
        ? Math.min(base64Content.length, Math.ceil((2 * 1024 * 1024) * 4 / 3)) // ~2MB decoded
        : Math.min(base64Content.length, 350_000);

    const decoded = decodeBase64UnicodeSafe(takeBase64Chunk(base64Content, base64ChunkSize));
    if (!decoded) return [];

    if (type === 'csv') {
        return getCsvFieldValuesFromFirstNRecords(decoded, fieldName, maxRecords);
    }

    if (type === 'json' || type === 'jsonl' || type === 'application/json') {
        try {
            const rows = _parseJsonOrJsonlRows(decoded, maxRecords);
            const values = [];
            for (const row of rows) {
                if (!row || typeof row !== 'object') continue;
                const v = getFieldValue(row, fieldName);
                if (v === undefined || v === null) continue;
                if (raw) {
                    const collected = collectRawFieldValue(v);
                    if (collected == null) continue;
                    values.push(collected);
                } else {
                    const s = formatOriginalColumnPreview(v);
                    if (!s || !s.trim()) continue;
                    values.push(s);
                }
                if (values.length >= maxRecords) break;
            }
            return values;
        } catch (e) {
            try {
                const v = getCsvFieldValueFromFirstRecord(decoded, fieldName);
                return v ? [v] : [];
            } catch {
                return [];
            }
        }
    }

    return [];
}

/**
 * Row-aligned sample records for one upload-slot entry — full record objects
 * (header-keyed for CSV) rather than a single column's values, so a caller
 * can pair the example cell of record *i* with the other columns' values of
 * the same record (Map prompt template modal / shuffle).
 * File bytes are preferred (first N records). Library picks have no bytes, so
 * the fetched `sampleRecords` (or legacy single `sampleRecord`) are used.
 */
export function sampleRecordsFromUploadEntry(entry, maxRecords = 20) {
    if (!entry) return [];

    const fetchedRecords = () => {
        const records =
            Array.isArray(entry.sampleRecords) && entry.sampleRecords.length
                ? entry.sampleRecords
                : entry.sampleRecord
                  ? [entry.sampleRecord]
                  : [];
        return records.slice(0, maxRecords);
    };

    if (entry.content) {
        const type = (entry.format || '').toLowerCase();
        // Parquet bytes cannot be parsed in the browser — fall back to the
        // rows `get-file-columns` returned alongside the column names.
        if (type === 'parquet') return fetchedRecords();

        const isJsonLike = type === 'json' || type === 'jsonl' || type === 'application/json';
        const base64ChunkSize = isJsonLike
            ? Math.min(entry.content.length, Math.ceil((2 * 1024 * 1024 * 4) / 3)) // ~2MB decoded
            : Math.min(entry.content.length, 350_000);
        const decoded = decodeBase64UnicodeSafe(takeBase64Chunk(entry.content, base64ChunkSize));
        if (!decoded) return fetchedRecords();

        if (type === 'csv') {
            const rows = parseCsvRows(decoded, maxRecords + 1);
            if (rows.length < 2) return [];
            const headers = rows[0].map((header) => (header ?? '').trim());
            return rows.slice(1).map((row) =>
                Object.fromEntries(
                    headers
                        .map((header, index) => [header, row[index]])
                        .filter(([header]) => header)
                )
            );
        }

        if (isJsonLike) {
            const parsed = _parseJsonOrJsonlRows(decoded, maxRecords).filter(
                (row) => row && typeof row === 'object' && !Array.isArray(row)
            );
            return parsed.length ? parsed : fetchedRecords();
        }

        return fetchedRecords();
    }

    return fetchedRecords();
}

/** True when an upload entry has bytes or sample rows we can preview / sniff. */
export function uploadEntryHasPreviewSamples(entry) {
    if (!entry) return false;
    if (entry.content) return true;
    if (Array.isArray(entry.sampleRecords) && entry.sampleRecords.length) return true;
    return Boolean(entry.sampleRecord && Object.keys(entry.sampleRecord).length);
}

function valuesFromSampleRecords(records, fieldName, maxRecords, raw) {
    const values = [];
    for (const record of records) {
        const value = getFieldValue(record, fieldName);
        if (value == null) continue;
        if (raw) {
            const collected = collectRawFieldValue(value);
            if (collected == null) continue;
            values.push(collected);
        } else {
            const text = formatOriginalColumnPreview(value);
            if (!text || !text.trim()) continue;
            values.push(text);
        }
        if (values.length >= maxRecords) break;
    }
    return values;
}

/**
 * Preview / shuffle values for one upload-slot entry.
 * File bytes are preferred (first N records). Library picks have no bytes, so
 * the fetched `sampleRecords` (or legacy single `sampleRecord`) are used.
 *
 * Pass `{ raw: true }` for delimiter detection: JSON array cells stay arrays
 * and string list literals keep their brackets. The default path unwraps
 * primitive arrays for the Review Examples preview cards.
 */
export function fieldValuesFromUploadEntry(entry, fieldName, maxRecords = 20, { raw = false } = {}) {
    if (!fieldName || !entry) return [];

    if (entry.content) {
        const fromContent = getFieldValuesFromFirstNRecords({
            base64Content: entry.content,
            fileType: entry.format,
            fieldName,
            maxRecords,
            raw,
        });
        if (fromContent.length) return fromContent;
    }

    const records = Array.isArray(entry.sampleRecords) && entry.sampleRecords.length
        ? entry.sampleRecords
        : entry.sampleRecord
          ? [entry.sampleRecord]
          : [];
    return valuesFromSampleRecords(records, fieldName, maxRecords, raw);
}

